import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import Left from "@repo/icons/default/Left";
import Right from "@repo/icons/default/Right";
import { graphql } from "@repo/graphql-types";
import { useEffect, type JSX } from "react";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { PendingComponent } from "@/components/ui/loading/pending-component";

const ValidateRecipientDataQuery = graphql(/* GraphQL */ `
  query ValidateRecipientDataQuery($codUsuario: Int!, $codClinica: Int, $skipClinica: Boolean!) {
    usuarios(where: { codUsuario: { _eq: $codUsuario } }) {
      codUsuario
      nome
      sobrenome
      cpf
      email
      dataNascimento
    }
    usuariosProfissionais(where: { codUsuario: { _eq: $codUsuario } }) {
      celularPessoalMembro
      profissao
    }
    usuariosEnderecos: tbUsuariosEnderecos(
      where: { codUsuario: { _eq: $codUsuario } }
      limit: 1
    ) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
    }
    clinicas: tbClinicas(where: { codClinica: { _eq: $codClinica } }) @skip(if: $skipClinica) {
      codClinica
      razaoSocial
      cnpj
      nomeResponsavel
      cpfResponsavel
      emailResponsavel
      celularResponsavel
    }
    clinicasEnderecos: tbClinicasEnderecos(
      where: { codClinica: { _eq: $codClinica } }
      limit: 1
    ) @skip(if: $skipClinica) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
    }
  }
`);

interface MissingData {
  type: "personal" | "professional";
  label: string;
  route: string;
}

const ValidateDataPage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const router = useRouter();

  const queryData = useGraphQL(ValidateRecipientDataQuery, {
    codUsuario: user.codUsuario,
    codClinica: user.codClinica ?? undefined,
    skipClinica: !user.codClinica,
  });

  const { data } = queryData;

  const getMissingData = (): MissingData[] => {
    if (!data) return [];

    const missing: MissingData[] = [];
    const usuario = data.usuarios[0];
    const profissional = data.usuariosProfissionais[0];
    const enderecoUsuario = data.usuariosEnderecos[0];
    const clinica = data.clinicas?.[0];
    const enderecoClinica = data.clinicasEnderecos?.[0];

    const isCompany = Boolean(user.codClinica);

    if (!usuario.dataNascimento || !usuario.email) {
      missing.push({
        type: "personal",
        label: "Complete os seus dados pessoais",
        route: "/settings/profile/personal-data",
      });
    }

    if (!profissional.celularPessoalMembro || !profissional.profissao) {
      missing.push({
        type: "professional",
        label: "Complete os seus dados profissionais",
        route: "/settings/profile/professional-data",
      });
    }

    const endereco = isCompany ? enderecoClinica : enderecoUsuario;
    if (!endereco) {
      missing.push({
        type: "personal",
        label: "Complete os seus dados pessoais",
        route: "/settings/profile/personal-data",
      });
    }

    // Validar dados da clínica (se for PJ)
    if (isCompany && (!clinica?.cnpj || !clinica.razaoSocial)) {
      missing.push({
        type: "professional",
        label: "Complete os seus dados profissionais",
        route: "/settings/profile/professional-data",
      });
    }

    return missing;
  };

  const missingData = getMissingData();
  const hasMissingData = missingData.length > 0;

  // caso volte para esta tela ele vai ser redirecionado para creater-recipient automatico
  useEffect(() => {
    if (!hasMissingData) {
      void navigate({ to: "/settings/payments/create-recipient", replace: true });
    }
  }, [hasMissingData, navigate]);

  const handleNavigateToForm = (route: string): void => {
    void router.navigate({ to: route });
  };

  const uniqueMissingData = missingData.filter(
    (item, index, self) => index === self.findIndex((t) => t.type === item.type),
  );

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Cadastrar conta" align="center" />
      </HeaderRoot>
      <Page className="px-6 py-8">
        <AsyncDataWrapper {...queryData}>
          {data ? (
            <EmptyStateRoot>
              <EmptyStateIcon icon={ExclamationCircle} />
              <EmptyStateTitle>
                Foram identificadas informações obrigatórias não preenchidas em seu
                cadastro
              </EmptyStateTitle>
              <EmptyStateDescription>
                Verifique abaixo, complete os dados necessários e tente novamente.
              </EmptyStateDescription>

              <div className="flex flex-col gap-3 w-full mt-6">
                {uniqueMissingData.map((item) => (
                  <button
                    type="button"
                    key={item.label}
                    onClick={() => handleNavigateToForm(item.route)}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-neutral-200 hover:bg-neutral-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-50 flex items-center justify-center">
                        <ExclamationCircle className="w-4 h-4 fill-primary-400" />
                      </div>
                      <span className="text-neutral-700 font-medium text-sm">
                        {item.label}
                      </span>
                    </div>
                    <Right className="w-4 h-4 fill-neutral-300" size={20} />
                  </button>
                ))}
              </div>

              <EmptyStateButton
                fullWidth
                onClick={() =>
                  void router.navigate({ to: "/settings/payments/create-recipient" })
                }
                className="mt-6"
              >
                Tentar novamente
              </EmptyStateButton>
            </EmptyStateRoot>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/create-recipient/validate-data")(
  {
    component: ValidateDataPage,
    pendingComponent: () => <PendingComponent title="Cadastrar conta" />,
  },
);
